import React, { forwardRef, MutableRefObject, useEffect, useRef } from 'react'
import CN from 'classnames'

import './checkbox.scss'

interface CheckboxProps {
  checkboxClassName?: string | undefined
  className?: string | undefined
  defaultChecked?: boolean
  helperText?: string | undefined
  id?: string | undefined
  indeterminate?: boolean
  isCustomStyles?: boolean
  isDisabled?: boolean
  isInvalid?: boolean
  labelText?: string | undefined
  labelTextClassName?: string | undefined
  onChange?: (e: any) => void
  onClick?: (e: any) => void
  [x: string]: any
}

const useCombinedRefs = (...refs: any[]): MutableRefObject<any> => {
  const targetRef = useRef()

  useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return

      if (typeof ref === 'function') {
        ref(targetRef.current)
      } else {
        ref.current = targetRef.current
      }
    })
  }, [refs])

  return targetRef
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checkboxClassName,
      className,
      defaultChecked,
      helperText,
      id,
      indeterminate,
      isCustomStyles,
      isDisabled,
      isInvalid,
      labelText,
      labelTextClassName,
      onChange,
      onClick,
      ...restProps
    }: CheckboxProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    /** check box main class name*/
    const checkBoxMainClass = !isCustomStyles
      ? CN('flex w-fit items-start cursor-pointer', className)
      : className

    /**set checkbox style class name*/
    const CheckboxClasses = CN('checkbox m-1', checkboxClassName, {
      'checkbox--is-indeterminate': indeterminate,
    })

    /**set checkbox colors class name*/
    const CheckboxSpanClass = CN({
      'checkbox--checkmark-yellow': !isInvalid,
      'checkbox--checkmark-red': isInvalid,
    })

    const defaultRef = React.useRef(null)
    const combinedRef = useCombinedRefs(ref, defaultRef)

    useEffect(() => {
      if (combinedRef?.current) {
        combinedRef.current.indeterminate = indeterminate || false
      }
    }, [combinedRef, indeterminate])

    return (
      <label className={checkBoxMainClass} htmlFor={id}>
        {/* input container */}
        <div className={CheckboxClasses}>
          <input
            disabled={isDisabled}
            id={id}
            type='checkbox'
            ref={combinedRef}
            onChange={onChange}
            checked={defaultChecked}
            onClick={onClick && onClick}
            {...restProps}
          />
          <span className={CheckboxSpanClass} />
        </div>

        {/* label and helper text container */}
        {labelText && (
          <div className='checkbox__label flex flex-col ml-2'>
            {/* label text */}
            <span
              className={CN('LabelText flex text-N-700 ', labelTextClassName)}>
              {labelText}
            </span>

            {/* helper text */}
            {helperText && (
              <span className='flex w-full text-x-small text-N-400'>
                {helperText}
              </span>
            )}
          </div>
        )}
      </label>
    )
  },
)
export default Checkbox

Checkbox.defaultProps = {
  checkboxClassName: undefined,
  className: undefined,
  defaultChecked: undefined,
  helperText: undefined,
  id: undefined,
  indeterminate: false,
  isCustomStyles: false,
  isDisabled: false,
  isInvalid: false,
  labelText: undefined,
  labelTextClassName: undefined,
  onChange: undefined,
  onClick: undefined,
}
